import React from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import dayjs from "dayjs";

const formatPaymentStatus = (value) => {
  const v = (value || "").toLowerCase();
  if (v === "paid") return "Paid";
  if (v === "unpaid") return "Unpaid";
  if (v === "na") return "N/A";
  return value || "—";
};

const BondArchivesPaymentOverrideHistoryDialog = ({
  paymentOverrideHistoryOpen,
  setPaymentOverrideHistoryOpen,
  paymentOverrideHistorySearch,
  setPaymentOverrideHistorySearch,
  paymentOverrideHistoryLoading,
  paymentOverrideHistoryList,
}) => {
  return (
    <Dialog
      open={paymentOverrideHistoryOpen}
      onClose={() => setPaymentOverrideHistoryOpen(false)}
      maxWidth="md"
      fullWidth
      slotProps={{ paper: { sx: { maxHeight: "90vh" } }}}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>Payment Status Override History</DialogTitle>
      <DialogContent dividers>
        <TextField
          size="small"
          placeholder="Search by bond #, estate, law firm, overridden by, status..."
          value={paymentOverrideHistorySearch}
          onChange={(e) => setPaymentOverrideHistorySearch(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />
        {paymentOverrideHistoryLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 460 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ "& th": { backgroundColor: "#f5f5f5", fontWeight: 600 } }}>
                  <TableCell>Bond #</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Estate</TableCell>
                  <TableCell>Law Firm</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell>At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const q = (paymentOverrideHistorySearch || "").toLowerCase().trim();
                  const filtered = q
                    ? paymentOverrideHistoryList.filter((row) => {
                      const hay = [
                        row.bond_number,
                        row.bond_type,
                        row.estate_name,
                        row.lawfirm_name,
                        row.previous_status,
                        row.new_status,
                        row.overridden_by,
                        row.reason,
                      ]
                        .filter(Boolean)
                        .join(" ")
                        .toLowerCase();
                      return hay.includes(q);
                    })
                    : paymentOverrideHistoryList;
                  if (filtered.length === 0) {
                    return (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 3, color: "text.secondary" }}>
                          {paymentOverrideHistoryList.length === 0
                            ? "No overrides yet."
                            : "No matches for your search."}
                        </TableCell>
                      </TableRow>
                    );
                  }
                  return filtered.map((row) => (
                    <TableRow key={row.override_id} hover>
                      <TableCell>{row.bond_number || "—"}</TableCell>
                      <TableCell>{row.bond_type || "—"}</TableCell>
                      <TableCell>{row.estate_name || "—"}</TableCell>
                      <TableCell>{row.lawfirm_name || "—"}</TableCell>
                      <TableCell>{formatPaymentStatus(row.previous_status)}</TableCell>
                      <TableCell>{formatPaymentStatus(row.new_status)}</TableCell>
                      <TableCell sx={{ maxWidth: 220, whiteSpace: "pre-wrap" }}>
                        {row.reason || "—"}
                      </TableCell>
                      <TableCell>{row.overridden_by || "—"}</TableCell>
                      <TableCell>
                        {row.overridden_at
                          ? dayjs(row.overridden_at).format("MMM D, YYYY h:mm A")
                          : "—"}
                      </TableCell>
                    </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPaymentOverrideHistoryOpen(false)} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

BondArchivesPaymentOverrideHistoryDialog.propTypes = {
  paymentOverrideHistoryOpen: PropTypes.bool.isRequired,
  setPaymentOverrideHistoryOpen: PropTypes.func.isRequired,
  paymentOverrideHistorySearch: PropTypes.string.isRequired,
  setPaymentOverrideHistorySearch: PropTypes.func.isRequired,
  paymentOverrideHistoryLoading: PropTypes.bool.isRequired,
  paymentOverrideHistoryList: PropTypes.array.isRequired,
};

export default BondArchivesPaymentOverrideHistoryDialog;
